// Cornerstone Components (@cruglobal/cornerstone-components): Cru's cs-* custom elements.
// Import each component here as the port starts using it, so the bundle carries only the
// elements the app renders. Their styles and tokens come from application.tailwind.css,
// which loads the package's Cru theme.
import "@cruglobal/cornerstone-components/components/animation/animation.js"
import "@cruglobal/cornerstone-components/components/avatar/avatar.js"
import "@cruglobal/cornerstone-components/components/badge/badge.js"
import "@cruglobal/cornerstone-components/components/breadcrumb/breadcrumb.js"
import "@cruglobal/cornerstone-components/components/breadcrumb-item/breadcrumb-item.js"
import "@cruglobal/cornerstone-components/components/button/button.js"
import "@cruglobal/cornerstone-components/components/button-group/button-group.js"
import "@cruglobal/cornerstone-components/components/callout/callout.js"
import "@cruglobal/cornerstone-components/components/card/card.js"
import "@cruglobal/cornerstone-components/components/color-picker/color-picker.js"
import "@cruglobal/cornerstone-components/components/details/details.js"
import "@cruglobal/cornerstone-components/components/dialog/dialog.js"
import "@cruglobal/cornerstone-components/components/divider/divider.js"
import "@cruglobal/cornerstone-components/components/drawer/drawer.js"
import "@cruglobal/cornerstone-components/components/dropdown/dropdown.js"
import "@cruglobal/cornerstone-components/components/dropdown-item/dropdown-item.js"
import "@cruglobal/cornerstone-components/components/icon/icon.js"
import "@cruglobal/cornerstone-components/components/input/input.js"
import "@cruglobal/cornerstone-components/components/number-input/number-input.js"
import "@cruglobal/cornerstone-components/components/option/option.js"
import "@cruglobal/cornerstone-components/components/page/page.js"
import "@cruglobal/cornerstone-components/components/popover/popover.js"
import "@cruglobal/cornerstone-components/components/progress-ring/progress-ring.js"
import "@cruglobal/cornerstone-components/components/radio/radio.js"
import "@cruglobal/cornerstone-components/components/radio-group/radio-group.js"
import "@cruglobal/cornerstone-components/components/relative-time/relative-time.js"
import "@cruglobal/cornerstone-components/components/resize-observer/resize-observer.js"
import "@cruglobal/cornerstone-components/components/select/select.js"
import "@cruglobal/cornerstone-components/components/spinner/spinner.js"
import "@cruglobal/cornerstone-components/components/switch/switch.js"
import "@cruglobal/cornerstone-components/components/tag/tag.js"
import "@cruglobal/cornerstone-components/components/textarea/textarea.js"
import "@cruglobal/cornerstone-components/components/tooltip/tooltip.js"

// FD-1338: the layout puts cs-cloak on <html> so the first paint waits for the components.
// Every import above has defined its element by now, so the cloak comes off here. It must:
// Cornerstone's loader is what normally removes it, and we import components ourselves.
// While it is on, any element nothing defines hides the whole page for up to 2 seconds,
// and Action Text renders an image in a description as one (<action-text-attachment>).
document.documentElement.classList.remove("cs-cloak")

// FD-568: an OPEN menu or popover is kept out of Turbo morphs. A broadcast refresh lands on
// every commit in the project, and morphing an open menu back to its server markup closed it
// and removed the item under the cursor, so a click on Delete did nothing. FD-1296 made this
// cover every open cs-dropdown, not just dropdown-load's: the menus rendered whole (the peek
// footer's ⋯, a page's More, the workspace switcher, the account menu) closed under the
// person using them on every refresh. FD-1298 adds cs-popover, which holds forms (the work
// items toolbar's Filters, and the work item add forms as they move): a morph would close it
// and throw away what was typed. What the open overlay shows can go stale, and stays so after
// it closes until the next refresh, which is fine for something this short-lived. One
// listener for the whole page: turbo:before-morph-element fires for every element in the
// morphed tree, and skipping the host skips its whole subtree.
//
// A cs-popover only reaches this guard if its markup gives it an id. Without one it gives
// itself a random id when it connects, so the morph can't match it to the server's copy: it
// builds a new popover instead, moves any id'd element out of the open one (the Filters
// body's turbo-frame), and leaves the open one behind, empty.
const OVERLAYS = new Set([ "cs-dropdown", "cs-popover" ])

document.addEventListener("turbo:before-morph-element", (event) => {
  const el = event.target
  if (OVERLAYS.has(el.localName) && el.open) event.preventDefault()
})

// FD-1363: a cs-input marked data-morph-reset takes the server's value when the page is
// morphed, the way a native <input> does. Idiomorph syncs a native input's value, but a
// cs-input keeps what was typed in its `value` property, and the server's `value` attribute
// only sets its default, so a morph left the typed text on screen. On the routing keys page
// every save redirects back to the page and morphs it: type an environment into one row,
// save another form, and the page said "Routing key updated." beside text it never saved.
//
// OPT-IN, by the marker only. Other pages may rely on a cs-input keeping what was typed
// through a broadcast refresh, so an unmarked one is left exactly as before. A marked one
// gives up typed text on ANY morph, a broadcast refresh included, as a native input does.
// turbo:morph-element fires after the element's attributes are synced, so the value read
// here is the server's. A field inside an open overlay is never reached: the FD-568 guard
// above skips the overlay's whole subtree.
document.addEventListener("turbo:morph-element", (event) => {
  const { currentElement, newElement } = event.detail
  if (currentElement.localName !== "cs-input" || !currentElement.hasAttribute("data-morph-reset")) return
  currentElement.value = newElement.getAttribute("value") ?? ""
})

// FD-1354: so a form in a popover that saves something closes it and clears itself once it
// is sent. The guard above keeps the open popover out of the refresh that follows, so it
// stayed open with what was typed still in it, and a second click on Add added it twice.
// The <details> menus these replace closed, because that refresh dropped their `open`.
// It closes unless the request failed outright: after a network error it stays open with
// what was typed, to try again. A save cut off by a broadcast refresh landing first closes
// too: Turbo stops the form's request and reports no result, though the server has it, and
// the refresh shows the new row.
// Focus goes back to the popover's button, as Escape does, unless the person has moved on
// while the save was out. Turbo disables the button that sent the form until the answer
// comes, which leaves focus on <body>, so <body> counts as still in the form, unless they have
// clicked somewhere since it was sent (a click on plain text also leaves focus on <body>). A
// GET form is left alone: it asks for a page rather than saving anything, and that page
// replaces this one (the toolbar's Filters). A popover meant to stay open after a save, such as
// the epic page's Add existing items (FD-956), which reloads its list after each add, needs a
// way out of this when it moves to a cs-popover.
// FD-1355: the popover can also sit inside the form, as a comment's reaction palette does: its
// emoji buttons submit the comment's one shared form (FD-495), which holds the chips as well.
// So the popover is found from the button that sent the form too: without it, the palette
// stayed open and focus stayed on <body>, where the busy emoji left it. A chip, outside the
// palette, has none, and leaves an open palette alone. A reaction's answer redraws only the
// chips, never the button this focuses, so focus stays put.
const sentAt = new WeakMap()
let pointerDownAt = 0

document.addEventListener("pointerdown", () => { pointerDownAt = performance.now() }, true)
document.addEventListener("turbo:submit-start", (event) => sentAt.set(event.target, performance.now()))

document.addEventListener("turbo:submit-end", (event) => {
  const form = event.target
  const popover = form.closest("cs-popover") ?? event.detail.formSubmission?.submitter?.closest("cs-popover")
  if (!popover || event.detail.success === false || form.method === "get") return
  const focus = document.activeElement
  const clickedSince = pointerDownAt > (sentAt.get(form) ?? Infinity)
  const returnFocus = popover.contains(focus) || ((!focus || focus === document.body) && !clickedSince)
  form.reset()
  popover.hide()
  if (returnFocus) document.getElementById(popover.getAttribute("for"))?.focus({ preventScroll: true })
})

// FD-1354: a field marked `data-autofocus` in a cs-popover gets `autofocus` only while its
// popover opens, and cs-popover focuses it. A real `autofocus` in a closed popover is seen by
// everything that looks for one: a cs-drawer's show() took the work item add row's hidden
// field, failed to focus it, and so skipped focusing itself, leaving focus on the drawer's
// first link (Full screen), where Enter left the list. Turbo looks for it after every render
// too. Set on cs-show, which comes before cs-popover looks for it; taken off on cs-hide.
// FD-1355: and taken off again as soon as cs-popover has used it. Turbo focuses a frame's
// [autofocus] after each frame render, and the morph guard above keeps an open popover, its
// autofocus with it, through a frame that reloads by morphing (the activity feed): every
// broadcast refresh moved focus onto an open comment palette's 👍, even from the comment box,
// and the next space reacted 👍. cs-popover looks for [autofocus] in the animation frame after
// cs-show. A frame callback registered here runs ahead of it in that same frame, so the
// removal waits for a task queued from it, which runs once the frame's callbacks are done.
document.addEventListener("cs-show", (event) => {
  if (event.target.localName !== "cs-popover") return
  const field = event.target.querySelector("[data-autofocus]")
  if (!field) return
  field.setAttribute("autofocus", "")
  requestAnimationFrame(() => setTimeout(() => field.removeAttribute("autofocus")))
})

document.addEventListener("cs-hide", (event) => {
  if (event.target.localName !== "cs-popover") return
  event.target.querySelector("[data-autofocus][autofocus]")?.removeAttribute("autofocus")
})

// FD-1353: a cs-tooltip on the button that opens a popover or menu stays up once it opens,
// above it, and Cornerstone gives Escape to the topmost overlay only. So the first Escape
// closed the tooltip and left Filters open. The tooltip is switched off while its overlay is
// open, and only a tooltip switched off here is switched back on.
// FD-1355: switched back on, it is closed too. Focus comes back to the button while the
// tooltip is still off (Escape puts it there, and so does a save in a popover), and its focus
// handler still sets `open`, then shows nothing because it is disabled. Left that way, `open`
// outlived the switch-off, and cs-tooltip's show() does nothing for a tooltip already open,
// so pointing at the button showed no tooltip until focus left it. The attribute, not the
// property, so a copy of the markup (closeOverlaysIn) comes back closed as well.
function triggerTooltip(overlay) {
  const triggerId = overlay.localName === "cs-popover"
    ? overlay.getAttribute("for")
    : overlay.querySelector(":scope > [slot='trigger']")?.id
  return triggerId ? document.querySelector(`cs-tooltip[for="${CSS.escape(triggerId)}"]`) : null
}

function releaseTooltip(tooltip) {
  if (!tooltip?.hasAttribute("data-overlay-open")) return
  tooltip.removeAttribute("data-overlay-open")
  tooltip.removeAttribute("open")
  tooltip.removeAttribute("disabled")
}

function switchOffTooltip(overlay) {
  const tooltip = triggerTooltip(overlay)
  if (!tooltip || tooltip.disabled) return
  tooltip.setAttribute("data-overlay-open", "")
  tooltip.disabled = true
}

document.addEventListener("cs-show", (event) => {
  if (OVERLAYS.has(event.target.localName)) switchOffTooltip(event.target)
})

document.addEventListener("cs-after-hide", (event) => {
  if (OVERLAYS.has(event.target.localName)) releaseTooltip(triggerTooltip(event.target))
})

// FD-1354: a refresh that lands while an overlay is open puts its button's tooltip back as
// the server draws it, switched on: the guard above keeps the open overlay out of the morph,
// but not the tooltip beside it, which the morph replaces. The tooltip then showed when Escape
// put focus back on the button, and took the next Escape, so closing a peek took three. So
// after each refresh, every overlay still open has its tooltip switched off again.
for (const rendered of [ "turbo:morph", "turbo:frame-render" ]) {
  document.addEventListener(rendered, () => {
    document.querySelectorAll(":is(cs-popover, cs-dropdown)[open]").forEach(switchOffTooltip)
  })
}

// FD-1353: Escape while a <select>'s drop-down list is open in a popover or menu belongs to the
// list. Chrome draws the list in the page, so that Escape reaches the document too, and
// Cornerstone closes its overlay on any Escape that does: all of Filters closed with a date
// field's Any / Before / After list. Stopped before the document sees it, the browser still
// closes the list and puts focus back on the select, and the next Escape closes the overlay,
// as Escape in a date field's calendar already did. A browser without :open keeps the old way.
window.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && selectListOpenInOverlay()) event.stopPropagation()
}, true)

function selectListOpenInOverlay() {
  try {
    return document.querySelector(":is(cs-popover, cs-dropdown)[open] select:open") !== null
  } catch {
    return false
  }
}

// FD-1353: Back restores Turbo's copy of the page as it was left, so a popover left open came
// back drawn open but deaf: Cornerstone wires its outside-click and Escape handling only when
// `open` changes, and a popover that starts open never sees a change, so only its own button
// closed it. closeOverlaysIn drops `open`, the tooltip switched off for it, and (FD-1354) the
// `autofocus` an open popover's field carries (see data-autofocus above) from markup about to
// be shown again: the page Turbo is about to render here, and the peek's cached panels
// (peek_controller, FD-1354), which a refresh copies with a popover open. It drops a
// cs-dropdown's `open` too: one that starts open opens itself, menu and focus, so the peek
// footer's ⋯ copied open popped open on every reopen of that item.
// Not on turbo:before-cache: a refresh fires that too when Turbo holds a copy of the current
// page (after a Back), and closing the live popovers there would close Filters mid-edit.
// A refresh's incoming page is the server's markup, which never has any of them.
export function closeOverlaysIn(root) {
  root.querySelectorAll(":is(cs-popover, cs-dropdown)[open]").forEach((overlay) => overlay.removeAttribute("open"))
  root.querySelectorAll("cs-popover [data-autofocus][autofocus]").forEach((field) => field.removeAttribute("autofocus"))
  root.querySelectorAll("cs-tooltip[data-overlay-open]").forEach(releaseTooltip)
}

document.addEventListener("turbo:before-render", (event) => closeOverlaysIn(event.detail.newBody))
